import React, { useEffect, useRef, useState } from "react";
import { xmlSunat } from '../../components/features/XMLSUNAT.tsx';
import Alert from "../../components/ui/Alert.tsx";
import FormularioCredencialesSUNAT, { OrigenCredenciales } from "../../components/SUNAT/FormCredencialesSUNAT.tsx";
import { ShieldCheck, FileUp, Database, Send, Terminal } from "lucide-react";
import { getAuth } from "firebase/auth";
import { useProject } from "../../context/ProjectContext.tsx";
import { obtenerConfigCliente, guardarConfigCliente, SunatCredenciales } from "../../services/clienteConexionService.ts";



const ValidacionSUNAT = ({ setPage }: { setPage: (page: string) => void }) => {
  const { project } = useProject();
  const clienteId = project?.clienteId || "";

  const [numeroCDP, setNumeroCDP] = useState("");
  const [fechaEmision, setFechaEmision] = useState("");
  const [Monto, setMonto] = useState("");
  const [supplierID, setSupplierID] = useState("");
  const [tipoCDP, settipoCDP] = useState("");
  const [serieCDP, setserieCDP] = useState("");
  const [xmlContent, setXmlContent] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const [inputMode, setInputMode] = useState<"xml" | "manual">("xml");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [url, setUrl] = useState("");
  const [origenCredenciales, setOrigenCredenciales] = useState<OrigenCredenciales>("manual");
  const [tieneCredencialesGuardadas, setTieneCredencialesGuardadas] = useState(false);
  const [usuarioGuardado, setUsuarioGuardado] = useState("");
  const [urlGuardada, setUrlGuardada] = useState("");
  const [guardarCredenciales, setGuardarCredenciales] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const handleInputModeChange = (mode: "xml" | "manual") => {
    setInputMode(mode);

    // Resetear input file si existe
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Esto borra el archivo seleccionado
    }
  };


  const showAlert = (msg: string, type: "success" | "error" | "warning" = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 12000);
  };

  // Precarga si ya hay credenciales SUNAT guardadas para este cliente, y
  // las ofrece como origen por defecto (ver Configuración > SUNAT).
  useEffect(() => {
    if (!clienteId) return;
    obtenerConfigCliente<SunatCredenciales>(clienteId, "sunat")
      .then((config) => {
        setTieneCredencialesGuardadas(config.tieneCredencialesGuardadas);
        setUsuarioGuardado(config.usuario);
        setUrlGuardada(config.url || "");
        setOrigenCredenciales(config.tieneCredencialesGuardadas ? "guardadas" : "manual");
      })
      .catch((err) => console.error("Error cargando configuración SUNAT:", err));
  }, [clienteId]);


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "text/xml");

      // 1. Buscamos el ID real del comprobante (Serie-Número)
      const ID = xmlDoc.evaluate(
        "/*/*[local-name()='ID']", // El /*/* busca el ID que sea hijo directo del nodo raíz
        xmlDoc, null, XPathResult.STRING_TYPE, null
      ).stringValue;

      // 2. Buscamos el tipo de comprobante
      const tipo = xmlDoc.evaluate("/*/*[local-name()='InvoiceTypeCode']", xmlDoc, null, XPathResult.STRING_TYPE, null).stringValue;

      // 3. Fecha de emisión
      const fecha = xmlDoc.evaluate("/*/*[local-name()='IssueDate']", xmlDoc, null, XPathResult.STRING_TYPE, null).stringValue;

      // 4. Monto a pagar
      const monto = xmlDoc.evaluate(
        "/*/*[local-name()='LegalMonetaryTotal']/*[local-name()='PayableAmount']",
        xmlDoc,
        null,
        XPathResult.STRING_TYPE,
        null
      ).stringValue;
      // El resto de tu lógica para supplierID...
      const supplierID = xmlDoc.evaluate(
        "/*/*[local-name()='AccountingSupplierParty']/*[local-name()='Party']/*[local-name()='PartyIdentification']/*[local-name()='ID']",
        xmlDoc, null, XPathResult.STRING_TYPE, null
      ).stringValue;

      if (!ID && !tipo) {
        showAlert("Archivo NO valido", "error");
        return
      }

      const [serie, numero] = ID.split("-");
      const formatDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split("-");
        return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
      };

      const formattedDate = formatDate(fecha);

      const xml = xmlSunat(supplierID, tipo, serie, numero, formattedDate, monto);


      settipoCDP(tipo);
      setserieCDP(serie);
      setNumeroCDP(numero);
      setFechaEmision(formattedDate);
      setMonto(monto);
      setSupplierID(supplierID);
      setXmlContent(xml);

    };
    reader.readAsText(file);
  };




  const sendToSunat = async () => {
    // Reemplaza estos valores con los datos reales o extraídos del XML

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      showAlert("No se detectó una sesión activa. Por favor, reingresa al sistema.", "error");
      return;
    }
    if (!clienteId) {
      showAlert("Este proyecto no tiene un cliente asociado.", "error");
      return;
    }
    if (origenCredenciales === "manual" && (!username || !password)) {
      showAlert("Por favor, ingresa usuario y contraseña.", "error");
      return;
    }

    setEnviando(true);
    try {
      if (origenCredenciales === "manual" && guardarCredenciales) {
        await guardarConfigCliente(clienteId, "sunat", {
          usuario: username,
          contraseña: password,
          url: url.trim() || undefined
        });
      }

      const token = await user.getIdToken();

      const payload: Record<string, string> = {
        rucEmisor: supplierID,
        tipoCDP: tipoCDP,
        serieCDP: serieCDP,
        numeroCDP: numeroCDP,
        tipoDocIdReceptor: "-",
        fechaEmision: fechaEmision,
        importeTotal: Monto,
        app: "sunat",
      };

      // "Guardadas": el backend lee usuario/contraseña/url de
      // clientes/{cliente}/credenciales/sunat. "Manual": se envían tal
      // cual y NO se manda `cliente` (si ese doc no existe todavía, el
      // backend lo trataría como error); la URL es opcional y, si se deja
      // en blanco, el backend cae al valor por defecto del sistema.
      if (origenCredenciales === "guardadas") {
        payload.cliente = clienteId;
      } else {
        payload.usuario = username;
        payload.contraseña = password;
        if (url.trim()) payload.url = url.trim();
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/sunat-wsdl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      const result = await response.text(); // XML string
      console.log("Respuesta SUNAT:", result);

      // 👇 Parse the XML and extract the message
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(result, "text/xml");

      const messageNode = xmlDoc.getElementsByTagName("statusMessage")[0];

      const message = messageNode?.textContent ?? "Mensaje no encontrado";

      if (message.includes("es un comprobante de pago válido")) {
        showAlert(message, "success");
      } else if (message.includes("Error en los criterios de consulta ingresados - validar la informacion enviada")) {
        showAlert(message, "warning");
      } else {
        showAlert(message, "error");
      }


    } catch (error) {
      console.log("Error al enviar a SUNAT:", error);
      showAlert("Error al enviar la solicitud a SUNAT.", "error");
    } finally {
      setEnviando(false);
    }
  };

  const inputClass = "w-full bg-[rgb(var(--c-surface))] border border-white/10 rounded-xl p-3 text-white focus:border-cyan-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed";
  const labelClass = "block text-xs font-bold text-cyan-400 uppercase tracking-wider mb-2 mt-4 ml-1";

  return (
    <main className="min-h-screen p-4 md:p-8 text-white bg-transparent">
      {/* HEADER */}
      <header className="max-w-7xl mx-auto mb-10">
        <h1 className="text-4xl font-extrabold mt-6 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 tracking-tight">
          Validación SUNAT <span className="text-white/20 font-light">|</span> CPE
        </h1>
        <p className="text-gray-500 mt-2 font-light italic">"Consulta integrada de validez de comprobantes de pago electrónicos"</p>
      </header>

      <div className="max-w-7xl mx-auto">
        <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage("")} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

          {/* COLUMNA IZQUIERDA: CREDENCIALES SOL */}
          <section className="lg:col-span-4 bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-[2.5rem] shadow-2xl">
            <div className="flex items-center gap-3 mb-6 border-b border-white/5 pb-4">
              <div className="p-3 bg-cyan-500/20 rounded-2xl text-cyan-400"><ShieldCheck size={24} /></div>
              <h2 className="text-xl font-bold">Acceso SOL</h2>
            </div>
            <FormularioCredencialesSUNAT
              origenCredenciales={origenCredenciales} setOrigenCredenciales={setOrigenCredenciales}
              tieneCredencialesGuardadas={tieneCredencialesGuardadas} usuarioGuardado={usuarioGuardado}
              urlGuardada={urlGuardada}
              username={username} password={password} setUsername={setUsername} setPassword={setPassword}
              url={url} setUrl={setUrl}
              guardarCredenciales={guardarCredenciales} setGuardarCredenciales={setGuardarCredenciales}
            />
          </section>

          {/* COLUMNA DERECHA: PROCESAMIENTO */}
          <section className="lg:col-span-8 space-y-6">

            {/* CARD DE ENTRADA DE DATOS */}
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-[2.5rem] shadow-2xl">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8 border-b border-white/5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-500/20 rounded-2xl text-blue-400">
                    {inputMode === 'xml' ? <FileUp size={24} /> : <Database size={24} />}
                  </div>
                  <h2 className="text-xl font-bold">Datos del Comprobante</h2>
                </div>

                {/* Switcher de Modo */}
                <div data-theme="dark" className="flex bg-black/40 p-1 rounded-xl border border-white/10">
                  <button
                    onClick={() => handleInputModeChange("xml")}
                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${inputMode === 'xml' ? 'bg-cyan-600 text-white shadow-lg' : 'text-gray-500 hover:text-white'}`}
                  >
                    SUBIR XML
                  </button>
                  <button
                    onClick={() => handleInputModeChange("manual")}
                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${inputMode === 'manual' ? 'bg-cyan-600 text-white shadow-lg' : 'text-gray-500 hover:text-white'}`}
                  >
                    MANUAL
                  </button>
                </div>
              </div>

              {inputMode === 'xml' ? (
                <div className="animate-fadeIn p-6 border-2 border-dashed border-white/10 rounded-3xl text-center hover:border-cyan-500/50 transition-colors group">
                  <input type="file" accept=".xml" onChange={handleFileChange} className="hidden" id="xml-upload" ref={fileInputRef} />
                  <label htmlFor="xml-upload" className="cursor-pointer">
                    <FileUp size={48} className="mx-auto text-gray-600 group-hover:text-cyan-400 transition-colors mb-4" />
                    <p className="text-gray-400">Arrastra tu XML o <span className="text-cyan-400 font-bold">selecciona un archivo</span></p>
                  </label>
                </div>
              ) : null}

              {/* Grid de Campos (Se habilitan/deshabilitan según el modo) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div>
                  <label className={labelClass}>RUC Emisor</label>
                  <input className={inputClass} value={supplierID} onChange={e => setSupplierID(e.target.value)} disabled={inputMode !== "manual"} placeholder="20XXXXXXXXX" />
                </div>
                <div>
                  <label className={labelClass}>Tipo CDP</label>
                  <input className={inputClass} value={tipoCDP} onChange={e => settipoCDP(e.target.value)} disabled={inputMode !== "manual"} placeholder="01" />
                </div>
                <div>
                  <label className={labelClass}>Serie</label>
                  <input className={inputClass} value={serieCDP} onChange={e => setserieCDP(e.target.value)} disabled={inputMode !== "manual"} placeholder="F001" />
                </div>
                <div>
                  <label className={labelClass}>Número</label>
                  <input className={inputClass} value={numeroCDP} onChange={e => setNumeroCDP(e.target.value)} disabled={inputMode !== "manual"} placeholder="000123" />
                </div>
                <div>
                  <label className={labelClass}>Fecha Emisión</label>
                  <input className={inputClass} value={fechaEmision} onChange={e => setFechaEmision(e.target.value)} disabled={inputMode !== "manual"} placeholder="dd/mm/yyyy" />
                </div>
                <div>
                  <label className={labelClass}>Importe Total</label>
                  <input className={inputClass} value={Monto} onChange={e => setMonto(e.target.value)} disabled={inputMode !== "manual"} placeholder="100.00" />
                </div>
              </div>
            </div>

            {/* XML PREVIEW / SOAP UI */}
            <div className="bg-black/40 border border-white/10 rounded-[2.5rem] overflow-hidden shadow-inner">
              <div className="bg-white/5 px-6 py-3 flex items-center justify-between border-b border-white/10">
                <div className="flex items-center gap-2 text-xs font-mono text-cyan-400">
                  <Terminal size={14} /> XML PAYLOAD PARA SOAP UI
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(xmlContent)}
                  className="text-[10px] bg-white/10 hover:bg-white/20 px-2 py-1 rounded transition-colors uppercase font-bold"
                >
                  Copiar Body
                </button>
              </div>
              <textarea
                value={xmlContent}
                readOnly
                rows={8}
                className="w-full bg-transparent p-6 font-mono text-[11px] text-gray-400 outline-none resize-none"
              />
            </div>

            {/* BOTÓN DE ACCIÓN */}
            <button
              onClick={sendToSunat}
              disabled={enviando}
              className="w-full py-5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 rounded-3xl font-bold text-white shadow-xl shadow-cyan-900/40 transition-all flex items-center justify-center gap-3"
            >
              <Send size={20} />
              {enviando ? "Validando..." : "Validar Comprobante en SUNAT"}
            </button>

          </section>
        </div>
      </div>
    </main>
  );
};

export default ValidacionSUNAT;
